import { useState, useEffect, useCallback, useContext } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { fetchJson, fetchData, StatusError } from '~/lib/fetch';
import { CheckoutStateContext, CheckoutAPIContext } from '~/contexts/checkout';
import {
  ADDITIONAL_PAYMENT_METHODS,
  COUNTRIES_REQUIRE_POSTAL_CODE,
  DEFAULT_BILLING_DETAILS,
  STEP,
  CHECKOUT_ERRORS,
  PAYMENT_METHODS_URL,
} from '~/constants/checkout';
import { isFreebie, getBillingDetailsObj } from '~/lib/checkout/util';
import { useCart } from '~/lib/cart';
import { useEventDispatch } from '~/lib/events';
import { CartProps } from '~/types/cart';
import {
  BillingDetailsFormProps,
  BillingDetailsProps,
  PaymentMethodProps,
  SelectedPaymentType,
  UsePaymentProps,
} from '~/types/checkout';
import { UserObjectProps } from '~/types/user';

const { NEW_PAYMENT_METHOD } = ADDITIONAL_PAYMENT_METHODS;

export const useCheckoutState = () => useContext(CheckoutStateContext);
export const useCheckoutAPI = () => useContext(CheckoutAPIContext);

const defaultValidationState = () => {
  const state: Record<string, boolean> = {};
  Object.values(STEP).forEach((step) => {
    state[step] = false;
  });

  return state;
};

const fetchGeoIPBillingCountry = async (
  setBillingDetails: (details: BillingDetailsProps) => void,
) => {
  try {
    const { country: geoIPCountry } = await fetchJson(`/api/geoip_country/`, {
      method: 'GET',
      include: 'credentials',
    });

    // Save a re-render by not updating the state if we don't have a country
    if (!geoIPCountry) return;

    // Anon users will not have any billing data so use the default
    setBillingDetails({ ...DEFAULT_BILLING_DETAILS, geoIPCountry });
  } catch (error: any) {
    // We tried
  }
};

export function usePayment(
  user?: UserObjectProps,
  initialPaymentMethods?: Array<PaymentMethodProps>,
): UsePaymentProps {
  const dispatchEvent = useEventDispatch();
  const { mutate: asyncMutate } = useSWRConfig();
  const [isPaymentsLoaded, setIsPaymentsLoaded] = useState(false);
  const [userSessionId, setUserSessionId] = useState<number | undefined | null>(
    null,
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<SelectedPaymentType>(null);
  const [defaultPaymentMethod, setDefaultPaymentMethod] =
    useState<SelectedPaymentType>(null);
  const [paymentMethods, setPaymentMethods] = useState<
    Array<PaymentMethodProps>
  >([]);

  // Add payment methods to the SWR cache
  // This should not update if the user adds a payment method in another window
  // It might make the payment methods act weirdly
  useSWR(PAYMENT_METHODS_URL, fetchJson, {
    fallbackData: initialPaymentMethods,
  });

  const setPaymentMethod = useCallback(
    (paymentMethodId: SelectedPaymentType) => {
      setSelectedPaymentMethod(paymentMethodId);
      dispatchEvent('payment:selectedPM', paymentMethodId);
      setDefaultPaymentMethod(paymentMethodId);
    },
    [setSelectedPaymentMethod, dispatchEvent],
  );

  // Set default payment method upon loading payment methods
  // We need a user to fetch the payment methods and update when the user changes
  // Just so things don't break if payments are added elsewhere, do not update once set (unless user changes)
  // Note: useEffect cannot be async itself, we have to call async function inside
  useEffect(() => {
    async function _fetchPaymentMethods() {
      // If we have already fetched the payment methods, use them (only fetch once, see above)
      const fetchedPaymentMethods = Array.isArray(initialPaymentMethods)
        ? initialPaymentMethods
        : await asyncMutate(PAYMENT_METHODS_URL);

      if (!fetchedPaymentMethods) return;

      const defaultIndex = fetchedPaymentMethods.findIndex(
        (e: PaymentMethodProps) => e.is_default,
      );

      if (defaultIndex > 0) {
        fetchedPaymentMethods.unshift(
          fetchedPaymentMethods.splice(defaultIndex, 1)[0],
        );
      }

      const initialDefaultPM = (fetchedPaymentMethods?.[0]?.id ||
        NEW_PAYMENT_METHOD) as SelectedPaymentType;

      setPaymentMethods(fetchedPaymentMethods);
      setPaymentMethod(initialDefaultPM);
    }

    if (isPaymentsLoaded || !user?.id) return;

    _fetchPaymentMethods().finally(() => {
      setIsPaymentsLoaded(true);
    });
  }, [
    initialPaymentMethods,
    isPaymentsLoaded,
    setPaymentMethod,
    asyncMutate,
    user?.id,
  ]);

  // Reset isPaymentsLoaded if user changes
  useEffect(() => {
    if (userSessionId !== user?.id) {
      setUserSessionId(user?.id);
      setIsPaymentsLoaded(false);
    }
  }, [user?.id, userSessionId]);

  const submitPaymentMethod = async (
    paymentMethodId: SelectedPaymentType,
  ): Promise<Object> => {
    const formData = new FormData();

    if (
      paymentMethodId?.includes('card_') ||
      paymentMethodId?.includes('pm_')
    ) {
      formData.append('stripe_token', paymentMethodId);
      formData.append('stripe', 'stripe');
    } else {
      formData.append('payment_method', paymentMethodId ?? '');
    }

    return fetchJson('/checkout/', {
      method: 'POST',
      include: 'credentials',
      body: formData,
    });
  };

  return {
    defaultPaymentMethod,
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod: setPaymentMethod,
    submitPaymentMethod,
    isPaymentsLoaded,
  };
}

export function useBilling(
  user: UserObjectProps | null = null,
  isUserLoading: boolean = false,
) {
  const { mutateItems } = useCart();
  const [billingDetails, setBillingDetails] = useState<BillingDetailsProps>(
    getBillingDetailsObj(user),
  );
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [hasFetchedGeoIP, setHasFetchedGeoIP] = useState(false);
  const [submittedBillingDetails, setSubmittedBillingDetails] =
    useState<string>('');
  const [userSessionId, setUserSessionId] = useState<number | undefined>();

  useEffect(() => {
    // Null user is anon user
    if (user === null && !isUserLoading && !hasFetchedGeoIP) {
      setHasFetchedGeoIP(true);
      fetchGeoIPBillingCountry(setBillingDetails);
    }

    if (!user?.id || userSessionId === user?.id) return;

    const updatedBillingDetails = getBillingDetailsObj(user);

    setBillingDetails(updatedBillingDetails);
  }, [user, userSessionId, hasFetchedGeoIP, isUserLoading]);

  // Reset isPaymentsLoaded if user changes
  useEffect(() => {
    if (userSessionId !== user?.id) {
      setUserSessionId(user?.id);
    }
  }, [user?.id, userSessionId]);

  const submitBillingDetails = async (
    data: BillingDetailsFormProps,
    showTaxCollection: boolean = false,
  ): Promise<[null | Error | StatusError, null | Response]> => {
    const formData = new FormData();
    const country = data?.country?.value || '';
    const needsPostalCode = COUNTRIES_REQUIRE_POSTAL_CODE.includes(
      `${country}`,
    );

    formData.append('country', `${country}`);

    if (needsPostalCode) {
      formData.append('postal_code', data?.postal_code);
    }

    if (country === 'AU' && showTaxCollection) {
      formData.append('tax_id', data?.tax_id ?? '');
      formData.append('tax_business_name', data?.tax_business_name ?? '');
    }

    if (data?.save_billing_info) {
      formData.append('set_billing_default', data.save_billing_info.toString());
      // TODO: Remove this (and update backend), it was decided not to have independent saving of tax id
      formData.append('set_tax_default', data.save_billing_info.toString());
    }

    // Check if the billing details have changed before submitting
    // This is to prevent unnecessary API calls if nothing has changed
    if (submittedBillingDetails === JSON.stringify(data)) {
      return Promise.resolve([null, null]);
    }

    setIsBillingLoading(true);

    return fetchData('/api/users/update_billing_info/', {
      method: 'POST',
      include: 'credentials',
      body: formData,
    })
      .then((response) => {
        setIsBillingLoading(false);

        if (response?.status === 200) {
          setBillingDetails({
            country: data?.country,
            postalCode: needsPostalCode ? data?.postal_code : '',
            taxId: data?.tax_id ?? '',
            taxBusinessName: data?.tax_business_name ?? '',
            // showTaxCollection and saveBillingInfo are used to control the UI state of the billing section
            showTaxCollection,
            saveBillingInfo: !!data?.save_billing_info,
          });

          setSubmittedBillingDetails(JSON.stringify(data));
          mutateItems();

          // Typescript is incorrectly assuming that is this correct (Response | null)[]
          return [null, response] as [null, Response];
        }

        // Added for typescript linting. This shouldn't happen since fetchData should throw an error if not 200
        throw new Error(
          `Cannot update billing details at this time. Please try again later. ${
            response?.status ?? 500
          } error.`,
        );
      })
      .catch((error) => {
        setIsBillingLoading(false);
        return [error, null];
      });
  };

  return {
    submitBillingDetails,
    billingDetails,
    isBillingLoading,
  };
}

export function useValidation() {
  const [validationState, setValidationState] = useState<Record<STEP, boolean>>(
    defaultValidationState(),
  );

  const setStepValidation = useCallback(
    (step: STEP, isValid: boolean) => {
      setValidationState((prevState) => ({
        ...prevState,
        [step]: isValid,
      }));
    },
    [setValidationState],
  );

  function getStepValidation(step: STEP) {
    return validationState[step];
  }

  function areAllStepsValid(cart?: CartProps): boolean {
    if (!cart) return false;

    const stepKeys = Object.keys(validationState);
    const validSteps = stepKeys.filter(
      (step) =>
        (step === STEP.BILLING && isFreebie(cart)) ||
        step === STEP.CREDITS_COUPONS || // Nothing to validate for the credits/coupons step
        validationState[step as STEP],
    );

    return validSteps.length === stepKeys.length;
  }

  return {
    setStepValidation,
    getStepValidation,
    areAllStepsValid,
  };
}

export function useReCaptcha() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(async (): Promise<
    [string, string]
  > => {
    if (!executeRecaptcha) {
      return ['', CHECKOUT_ERRORS.RECAPTCHA_NOT_LOADED];
    }

    try {
      const token = await executeRecaptcha('submit');

      return [token, ''];
    } catch (error) {
      return ['', CHECKOUT_ERRORS.RECAPTCHA_ERROR];
    }
  }, [executeRecaptcha]);

  // You can use useEffect to trigger the verification as soon as the component being loaded
  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  return { handleReCaptchaVerify };
}
